import { useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Download, FileSpreadsheet, FileText } from 'lucide-react'
import { cx } from '@/lib/clases'
import { formatearMonto } from '@/lib/monto'
import { formatearFecha } from '@/lib/fecha'
import { Boton } from '@/ui/Boton'
import { Banner } from '@/ui/Banner'
import { EstadoVacio } from '@/ui/EstadoVacio'
import { Esqueleto } from '@/ui/Esqueleto'
import { Monto } from '@/ui/Numero'
import { traducirErrorApi } from '@/dominio/documentos/errores'
import { useUbicaciones } from '@/dominio/documentos/useDocumentos'
import {
  PESTANAS,
  esFormato,
  esPestana,
  type Fila606,
  type Fila609,
  type FormatoDescarga,
  type FormatoRevision,
  type PestanaReporte,
  type TipoDeclaracion,
  type TipoReporte,
} from '@/dominio/reportes/tipos'
import {
  descargarArchivoReporte,
  descargarAyudaDeclaracion,
  useAyudaDeclaracion,
  useLineasReporte,
  useValidarReporte,
} from '@/dominio/reportes/useReportes'
import { EncabezadoPantalla, Tarjeta } from './-pantalla'
import { ListaInconsistencias } from './-reportes/ListaInconsistencias'
import { PanelComplemento } from './-reportes/PanelComplemento'
import { VistaDeclaracion } from './-reportes/VistaDeclaracion'

/**
 * Reportes fiscales (E36): los cuatro formatos de envío y las ayudas para
 * llenar el IR-17 y el IT-1.
 *
 * El mismo patrón en las cuatro pestañas de formato: selector de período, botón
 * de validar, resultado, y tres descargas: «TXT para la DGII», el único que se
 * envía, y el PDF y el Excel, las mismas filas para revisarlas. Y las dos reglas:
 *
 *   **validar antes de descargar el TXT, siempre.** Se habilita aunque haya
 *   avisos y se bloquea con errores duros. El PDF y el Excel no esperan: no se
 *   envían, y revisar es lo que se hace antes de corregir
 *   **cada inconsistencia es un enlace al documento que la causa.** Un reporte
 *   que dice "hay 4 errores" sin decir dónde obliga a buscarlos a mano
 *
 * Lo que el 606 y el 609 piden de los e-CF 41, 43 y 47 y su XML no lleva se
 * completa aquí mismo, en un panel: mandar al contador a otra pantalla le haría
 * volver a buscar el período.
 *
 * Las pestañas del IR-17 y el IT-1 **no presentan nada**. Calculan lo que la
 * plataforma sabe del mes en el orden del formulario, y lo dicen.
 */
export interface BusquedaReportes {
  reporte?: string
  periodo?: string
  /** Solo en el 607: la sucursal cuyo arqueo se cuadra. No cambia el archivo que se remite. */
  sucursal?: string
  /** Abre el panel para completar ese e-CF 41, 43 o 47. */
  documento?: string
}

export const Route = createFileRoute('/reportes')({
  validateSearch: (busqueda: Record<string, unknown>): BusquedaReportes => ({
    reporte: typeof busqueda.reporte === 'string' ? busqueda.reporte : undefined,
    periodo: typeof busqueda.periodo === 'string' ? busqueda.periodo : undefined,
    sucursal: typeof busqueda.sucursal === 'string' ? busqueda.sucursal : undefined,
    documento: typeof busqueda.documento === 'string' ? busqueda.documento : undefined,
  }),
  component: PantallaReportes,
})

function periodoActual(): string {
  const hoy = new Date()
  return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`
}

function PantallaReportes() {
  const navegar = useNavigate({ from: Route.fullPath })
  const busqueda = Route.useSearch()

  const pestana: PestanaReporte = esPestana(busqueda.reporte) ? busqueda.reporte : '606'
  const periodo = busqueda.periodo ?? periodoActual()
  const documento = busqueda.documento ?? null
  const descripcion = PESTANAS.find((p) => p.pestana === pestana)

  // Un enlace que llega con `documento` —desde el tablero o desde una
  // inconsistencia— viene a completar un pago del período: se valida solo, que
  // es lo que el usuario vino a ver.
  const [calculado, setCalculado] = useState(documento !== null)

  const cambiar = (cambios: BusquedaReportes): void => {
    setCalculado(false)
    void navegar({ search: (previo) => ({ ...previo, ...cambios, documento: undefined }), replace: true })
  }

  const abrirComplemento = (id: string): void => {
    void navegar({ search: (previo) => ({ ...previo, documento: id }), replace: true })
  }

  const cerrarComplemento = (): void => {
    void navegar({ search: (previo) => ({ ...previo, documento: undefined }), replace: true })
  }

  return (
    <div className="flex max-w-[960px] flex-col gap-4">
      <EncabezadoPantalla titulo="Reportes" />

      <nav aria-label="Reportes" className="border-b border-gris-300">
        <ul className="flex flex-wrap items-end gap-1">
          {PESTANAS.map((p, i) => (
            <li
              key={p.pestana}
              // Los formatos se remiten; las declaraciones se llenan en la Oficina
              // Virtual. La raya lo separa sin otra palabra.
              className={cx(i > 0 && PESTANAS[i - 1]?.clase !== p.clase && 'ml-3 border-l border-gris-300 pl-3')}
            >
              <button
                type="button"
                aria-current={pestana === p.pestana ? 'page' : undefined}
                onClick={() => cambiar({ reporte: p.pestana })}
                className={cx(
                  'inline-flex h-[var(--alto-control)] items-center border-b-2 px-3 text-cuerpo',
                  pestana === p.pestana
                    ? 'border-marca font-medium text-marca'
                    : 'border-transparent text-gris-600 hover:text-gris-900',
                )}
              >
                {p.titulo}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {esFormato(pestana) ? (
        <PestanaFormato
          tipo={pestana}
          periodo={periodo}
          sucursal={pestana === '607' ? (busqueda.sucursal ?? '') : ''}
          validado={calculado}
          onValidar={() => setCalculado(true)}
          onPeriodo={(valor) => cambiar({ periodo: valor })}
          onSucursal={(valor) => cambiar({ sucursal: valor === '' ? undefined : valor })}
          onCompletar={abrirComplemento}
          descripcion={descripcion?.descripcion}
        />
      ) : (
        <PestanaDeclaracion
          tipo={pestana}
          periodo={periodo}
          calculado={calculado}
          onCalcular={() => setCalculado(true)}
          onPeriodo={(valor) => cambiar({ periodo: valor })}
          onCompletar={abrirComplemento}
          descripcion={descripcion?.descripcion}
        />
      )}

      <PanelComplemento documento={documento} onCerrar={cerrarComplemento} />
    </div>
  )
}

function SelectorPeriodo({ periodo, onPeriodo }: { periodo: string; onPeriodo: (valor: string) => void }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-etiqueta font-medium text-gris-600">Período</span>
      <input
        type="month"
        value={periodo}
        onChange={(evento) => onPeriodo(evento.target.value)}
        className="numero h-[var(--alto-control)] rounded-md border border-gris-500 bg-white px-2 text-gris-900"
      />
    </label>
  )
}

/**
 * Las sucursales que vendieron en el período, para ver el 607 de una sola. Un
 * contribuyente sin caja no tiene ninguna, y no ve el selector.
 */
function SelectorSucursal({
  periodo,
  sucursal,
  onSucursal,
}: {
  periodo: string
  sucursal: string
  onSucursal: (valor: string) => void
}) {
  const { desde, hasta } = limitesDelPeriodo(periodo)
  const ubicaciones = useUbicaciones(desde, hasta)
  const sucursales = ubicaciones.data?.sucursales ?? []

  if (sucursales.length === 0 && sucursal === '') {
    return null
  }

  return (
    <label className="flex flex-col gap-1">
      <span className="text-etiqueta font-medium text-gris-600">Sucursal</span>
      <select
        value={sucursal}
        onChange={(evento) => onSucursal(evento.target.value)}
        className="h-[var(--alto-control)] rounded-md border border-gris-500 bg-white px-2 text-cuerpo text-gris-900"
      >
        <option value="">Todas (lo que se remite)</option>
        {sucursal !== '' && !sucursales.some((s) => s.codigo === sucursal) ? (
          <option value={sucursal}>{sucursal}</option>
        ) : null}
        {sucursales.map((s) => (
          <option key={s.codigo} value={s.codigo}>
            {s.nombre ? `${s.nombre} (${s.codigo})` : s.codigo}
          </option>
        ))}
      </select>
    </label>
  )
}

/** El primer y el último día de un período `aaaa-mm`, o vacíos si no se entiende. */
function limitesDelPeriodo(periodo: string): { desde: string; hasta: string } {
  const partes = /^(\d{4})-(\d{2})$/.exec(periodo)
  if (!partes) return { desde: '', hasta: '' }
  const anio = Number(partes[1])
  const mes = Number(partes[2])
  // El día 0 del mes siguiente es el último de este.
  const ultimo = new Date(Date.UTC(anio, mes, 0)).getUTCDate()
  return { desde: `${periodo}-01`, hasta: `${periodo}-${String(ultimo).padStart(2, '0')}` }
}

/* ------------------------------------------------------------------ */
/* Formatos de envío                                                   */
/* ------------------------------------------------------------------ */

function PestanaFormato({
  tipo,
  periodo,
  sucursal,
  validado,
  onValidar,
  onPeriodo,
  onSucursal,
  onCompletar,
  descripcion,
}: {
  tipo: TipoReporte
  periodo: string
  /** Solo el 607. Vacío es el contribuyente entero. */
  sucursal: string
  validado: boolean
  onValidar: () => void
  onPeriodo: (valor: string) => void
  onSucursal: (valor: string) => void
  onCompletar: (documento: string) => void
  descripcion: string | undefined
}) {
  const consulta = useValidarReporte(tipo, periodo, validado, sucursal)
  const resultado = consulta.data
  const [descargando, setDescargando] = useState<FormatoDescarga | null>(null)
  const [errorDescarga, setErrorDescarga] = useState<unknown>(null)

  // Lo decide el servidor: recalcularlo recorriendo las inconsistencias es la
  // misma regla escrita dos veces, y el dia que el backend anada un error duro
  // que no viaje en la lista la descarga se habilitaria con el reporte roto.
  const bloqueado = resultado?.hayErroresDuros === true

  // La vista de una sucursal no es lo que se remite: el TXT lleva el contribuyente entero, y lo que
  // se validó aquí es solo una parte. Para descargar hay que validar el 607 completo.
  const porSucursal = sucursal !== ''

  // Validado y con resultado, lo siguiente es bajar el TXT: el botón principal
  // pasa de «Validar» a él.
  const listo = validado && resultado !== undefined

  const descargar = (formato: FormatoDescarga): void => {
    setDescargando(formato)
    setErrorDescarga(null)
    descargarArchivoReporte(tipo, periodo, formato, sucursal)
      .catch((error: unknown) => setErrorDescarga(error))
      .finally(() => setDescargando(null))
  }

  return (
    <>
      <Tarjeta>
        <div className="flex flex-wrap items-end gap-4">
          <SelectorPeriodo periodo={periodo} onPeriodo={onPeriodo} />

          {tipo === '607' ? <SelectorSucursal periodo={periodo} sucursal={sucursal} onSucursal={onSucursal} /> : null}

          <Boton variante={listo ? 'secundario' : 'primario'} onClick={onValidar} disabled={consulta.isFetching}>
            {consulta.isFetching ? 'Validando…' : 'Validar'}
          </Boton>

          <div role="group" aria-label="Descargar el formato" className="flex flex-wrap items-end gap-2">
            <Boton
              variante={listo ? 'primario' : 'secundario'}
              icono={<Download />}
              onClick={() => descargar('txt')}
              // Se habilita con avisos; se bloquea con errores duros, y con la vista de una sucursal.
              disabled={!listo || bloqueado || descargando !== null || porSucursal}
              title={
                porSucursal
                  ? 'El TXT lleva todas las sucursales: quita el filtro y valida el 607 completo para descargarlo.'
                  : bloqueado
                    ? 'Resuelve los errores antes de descargar.'
                    : 'El archivo que se sube a la Oficina Virtual de la DGII.'
              }
            >
              {descargando === 'txt' ? 'Descargando…' : 'TXT para la DGII'}
            </Boton>

            {/*
              El PDF y el Excel no se envían: no esperan a validar ni se bloquean con
              errores, porque revisar el período es justo lo que se hace antes de
              corregirlo. Con sucursal salen solo de ella, y el archivo lo dice.
            */}
            <Boton
              icono={<FileText />}
              onClick={() => descargar('pdf')}
              disabled={descargando !== null}
              title={
                porSucursal
                  ? `Solo la sucursal ${sucursal}, para revisarla. No se envía a la DGII.`
                  : 'Para revisarlo o imprimirlo. No se envía a la DGII.'
              }
            >
              {descargando === 'pdf' ? 'Descargando…' : 'PDF'}
            </Boton>

            <Boton
              icono={<FileSpreadsheet />}
              onClick={() => descargar('xlsx')}
              disabled={descargando !== null}
              title={
                porSucursal
                  ? `Solo la sucursal ${sucursal}, para revisarla. No se envía a la DGII.`
                  : 'Para revisarlo con fórmulas y filtros. No se envía a la DGII.'
              }
            >
              {descargando === 'xlsx' ? 'Descargando…' : 'Excel'}
            </Boton>
          </div>

          <p className="text-etiqueta text-gris-600">{descripcion}</p>
        </div>
        <p className="pt-3 text-etiqueta text-gris-600">
          El TXT es el único archivo que se envía a la DGII. El PDF y el Excel llevan las mismas filas, para revisarlas.
        </p>
        {porSucursal ? (
          <p className="pt-3 text-etiqueta text-gris-600">
            Estás viendo solo la sucursal {sucursal}, para cuadrar su arqueo. La DGII pide el 607 por RNC: el archivo
            que se le envía lleva todas las sucursales.
          </p>
        ) : null}
      </Tarjeta>

      {consulta.isError ? (
        <Banner tono="rojo" titulo={traducirErrorApi(consulta.error).titulo}>
          {traducirErrorApi(consulta.error).contexto}
        </Banner>
      ) : null}

      {errorDescarga !== null ? (
        <Banner tono="rojo" titulo={traducirErrorApi(errorDescarga).titulo}>
          {traducirErrorApi(errorDescarga).contexto}
        </Banner>
      ) : null}

      {!validado ? (
        <Tarjeta relleno={false}>
          <EstadoVacio
            variante="resuelto"
            mensaje="Valida el período antes de descargar. El resultado lista las inconsistencias con enlace al documento que las causa."
          />
        </Tarjeta>
      ) : consulta.isFetching || resultado === undefined ? (
        <div className="flex flex-col gap-2">
          <Esqueleto className="h-9 w-full" />
          <Esqueleto className="h-9 w-2/3" />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <Tarjeta titulo={`Resultado · ${resultado.periodo}`}>
            <dl className="flex flex-wrap gap-8">
              <div>
                <dt className="text-etiqueta text-gris-600">Registros</dt>
                <dd className="numero text-[20px] text-gris-900">
                  {formatearMonto(resultado.documentos, { decimales: 0 })}
                </dd>
              </div>
              <div>
                <dt className="text-etiqueta text-gris-600">Monto</dt>
                <dd>
                  <Monto valor={resultado.monto} mostrarMoneda className="text-[20px] text-gris-900" />
                </dd>
              </div>
              <div>
                <dt className="text-etiqueta text-gris-600">Corte</dt>
                <dd className="numero text-[20px] text-gris-900">{formatearFecha(resultado.fechaCorte)}</dd>
              </div>
            </dl>

            {/* Si el período incluye migrados, se dice en una línea. */}
            {resultado.migrados > 0 ? (
              <p className="pt-3 text-cuerpo text-gris-600">
                Incluye {formatearMonto(resultado.migrados, { decimales: 0 })} documentos migrados.
              </p>
            ) : null}

            {/*
              El 606 muestra arriba cuántos siguen sin aprobación comercial y
              cuántos días faltan para el 15. Es el mismo dato de la pantalla de
              aprobaciones, visto desde el otro lado.
            */}
            {tipo === '606' && resultado.sinAprobacion > 0 ? (
              <Banner
                tono="ambar"
                className="mt-3"
                titulo={`${resultado.sinAprobacion} documentos siguen sin aprobación comercial`}
                accion={
                  <Link to="/recibidos">
                    <Boton variante="secundario" tamano="menor">
                      Ir a aprobaciones
                    </Boton>
                  </Link>
                }
              >
                {/*
                  `diasParaElCorte` puede ser negativo: el corte ya pasó. Aquí se
                  comparaba contra `null`, que el servidor no manda nunca, así
                  que un período cerrado seguía diciendo «quedan -3 días» — el
                  único caso en el que el usuario tiene que dejar de intentar
                  aprobar y declararlo en el 606 era justo el que no se decía.
                */}
                {resultado.diasParaElCorte < 0
                  ? `El corte del ${formatearFecha(resultado.fechaCorte)} ya pasó: estos documentos se resuelven en este mismo 606.`
                  : `Quedan ${resultado.diasParaElCorte} días para el corte del ${formatearFecha(resultado.fechaCorte)}.`}
              </Banner>
            ) : null}
          </Tarjeta>

          <Tarjeta titulo="Inconsistencias" relleno={false}>
            <ListaInconsistencias inconsistencias={resultado.inconsistencias} onCompletar={onCompletar} />
          </Tarjeta>

          {tipo === '606' ? <Autogenerados606 periodo={periodo} onCompletar={onCompletar} /> : null}
          {tipo === '609' ? <Pagos609 periodo={periodo} onCompletar={onCompletar} /> : null}

          <Tarjeta titulo="Historial de generaciones" relleno={false}>
            {/*
              Honesto: el historial exige que el servidor registre cada
              generación con su fecha y su usuario, y eso todavía no existe.
            */}
            <EstadoVacio
              variante="primera-vez"
              mensaje="El historial de generaciones necesita que el servidor registre cada descarga con su fecha y su usuario. Todavía no existe ese extremo."
            />
          </Tarjeta>
        </div>
      )}
    </>
  )
}

/**
 * Los e-CF 41, 43 y 47 que el contribuyente se emitió y van en su 606. Sus
 * columnas 3, 7, 17 y 23 no están en el XML: se completan aquí.
 */
function Autogenerados606({ periodo, onCompletar }: { periodo: string; onCompletar: (documento: string) => void }) {
  const lineas = useLineasReporte<Fila606>('606', periodo, true)
  const propias = (lineas.data?.filas ?? []).filter((f) => f.origen === 'autogenerado')

  if (lineas.isLoading) return <Esqueleto className="h-9 w-full" />
  if (propias.length === 0) return null

  return (
    <Tarjeta titulo="Comprobantes que emites tú (41, 43 y 47)" relleno={false}>
      <ul className="flex flex-col">
        {propias.map((fila) => {
          const falta = fila.tipoBienesServiciosComprados === null
          return (
            <li key={`${fila.id}-${fila.remision}`} className="flex items-center gap-3 border-b border-gris-200 px-4 py-2 last:border-b-0">
              <span className="numero w-[140px] shrink-0 text-gris-900">{fila.ncf}</span>
              <span className="min-w-0 flex-1 text-cuerpo text-gris-700">
                {fila.razonSocialEmisor ?? '—'} · {formatearFecha(fila.fechaComprobante)}
                {fila.remision === 'por-retencion' ? ' · segunda remisión por retención' : null}
                {fila.remision === 'retencion-diferida' ? ' · la retención va en el mes del pago' : null}
              </span>
              <span className="shrink-0">
                <Monto valor={fila.totalMontoFacturado} />
              </span>
              <Boton variante={falta ? 'primario' : 'secundario'} tamano="menor" onClick={() => onCompletar(fila.id)}>
                {falta ? 'Completar' : 'Editar'}
              </Boton>
            </li>
          )
        })}
      </ul>
    </Tarjeta>
  )
}

/** Los pagos al exterior del período, con lo que falta para remitir cada uno. */
function Pagos609({ periodo, onCompletar }: { periodo: string; onCompletar: (documento: string) => void }) {
  const lineas = useLineasReporte<Fila609>('609', periodo, true)
  const filas = lineas.data?.filas ?? []

  if (lineas.isLoading) return <Esqueleto className="h-9 w-full" />
  if (filas.length === 0) return null

  return (
    <Tarjeta titulo="Pagos al exterior del período" relleno={false}>
      <ul className="flex flex-col">
        {filas.map((fila) => (
          <li key={fila.id} className="flex items-start gap-3 border-b border-gris-200 px-4 py-2 last:border-b-0">
            <span className="numero w-[140px] shrink-0 text-gris-900">{fila.eNCF}</span>
            <span className="min-w-0 flex-1">
              <span className="block text-cuerpo text-gris-900">
                {fila.razonSocial ?? '—'}
                {fila.paisDestino ? <span className="text-gris-600"> · {fila.paisDestino}</span> : null}
              </span>
              <span className={cx('block text-etiqueta', fila.faltantes.length > 0 ? 'text-rojo' : 'text-gris-600')}>
                {fila.faltantes.length > 0 ? `Falta: ${fila.faltantes.join(', ')}.` : 'Completo.'}
              </span>
            </span>
            <span className="shrink-0 text-right">
              <Monto valor={fila.montoFacturado} />
              {fila.isrRetenido !== null ? (
                <span className="block text-etiqueta text-gris-600">
                  ISR <Monto valor={fila.isrRetenido} />
                </span>
              ) : null}
            </span>
            <Boton
              variante={fila.faltantes.length > 0 ? 'primario' : 'secundario'}
              tamano="menor"
              onClick={() => onCompletar(fila.id)}
            >
              {fila.faltantes.length > 0 ? 'Completar' : 'Editar'}
            </Boton>
          </li>
        ))}
      </ul>
    </Tarjeta>
  )
}

/* ------------------------------------------------------------------ */
/* Declaraciones                                                       */
/* ------------------------------------------------------------------ */

function PestanaDeclaracion({
  tipo,
  periodo,
  calculado,
  onCalcular,
  onPeriodo,
  onCompletar,
  descripcion,
}: {
  tipo: TipoDeclaracion
  periodo: string
  calculado: boolean
  onCalcular: () => void
  onPeriodo: (valor: string) => void
  onCompletar: (documento: string) => void
  descripcion: string | undefined
}) {
  const consulta = useAyudaDeclaracion(tipo, periodo, calculado)
  const [descargando, setDescargando] = useState<FormatoRevision | null>(null)
  const [errorDescarga, setErrorDescarga] = useState<unknown>(null)

  const descargar = (formato: FormatoRevision): void => {
    setDescargando(formato)
    setErrorDescarga(null)
    descargarAyudaDeclaracion(tipo, periodo, formato)
      .catch((error: unknown) => setErrorDescarga(error))
      .finally(() => setDescargando(null))
  }

  return (
    <>
      <Tarjeta>
        <div className="flex flex-wrap items-end gap-4">
          <SelectorPeriodo periodo={periodo} onPeriodo={onPeriodo} />

          <Boton variante="primario" onClick={onCalcular} disabled={consulta.isFetching}>
            {consulta.isFetching ? 'Calculando…' : 'Calcular'}
          </Boton>

          {/*
            Sin TXT: la ayuda no se envía, se copia en la Oficina Virtual. El PDF y
            el Excel son la misma ayuda para tenerla al lado mientras se llena.
          */}
          <div role="group" aria-label="Descargar la ayuda" className="flex flex-wrap items-end gap-2">
            <Boton
              icono={<FileText />}
              onClick={() => descargar('pdf')}
              disabled={descargando !== null}
              title="Para tenerla al lado mientras se llena. No se presenta desde aquí."
            >
              {descargando === 'pdf' ? 'Descargando…' : 'PDF'}
            </Boton>

            <Boton
              icono={<FileSpreadsheet />}
              onClick={() => descargar('xlsx')}
              disabled={descargando !== null}
              title="Para tenerla al lado mientras se llena. No se presenta desde aquí."
            >
              {descargando === 'xlsx' ? 'Descargando…' : 'Excel'}
            </Boton>
          </div>

          <p className="text-etiqueta text-gris-600">{descripcion}</p>
        </div>
      </Tarjeta>

      {consulta.isError ? (
        <Banner tono="rojo" titulo={traducirErrorApi(consulta.error).titulo}>
          {traducirErrorApi(consulta.error).contexto}
        </Banner>
      ) : null}

      {errorDescarga !== null ? (
        <Banner tono="rojo" titulo={traducirErrorApi(errorDescarga).titulo}>
          {traducirErrorApi(errorDescarga).contexto}
        </Banner>
      ) : null}

      {!calculado ? (
        <Tarjeta relleno={false}>
          <EstadoVacio
            variante="resuelto"
            mensaje="Calcula el período para ver las casillas. Es una ayuda para llenar la declaración: no se presenta desde aquí."
          />
        </Tarjeta>
      ) : consulta.isFetching || consulta.data === undefined ? (
        <div className="flex flex-col gap-2">
          <Esqueleto className="h-9 w-full" />
          <Esqueleto className="h-9 w-2/3" />
        </div>
      ) : (
        <VistaDeclaracion ayuda={consulta.data} onCompletar={onCompletar} />
      )}
    </>
  )
}
